import React, { useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  Button, 
  Space, 
  Row, 
  Col,
  Switch
} from 'antd';
import { 
  SaveOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from '../../hooks/useTranslation';
import { getSpecializationLabel } from '../../constants/specializations';
import { AdminUser } from './types';

const { Option } = Select;

interface AdminUserModalProps {
  visible: boolean;
  admin?: AdminUser | null;
  isSubmitting: boolean;
  form: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  doctors: any[];
  loadingDoctors: boolean;
  clinics: any[];
  loadingClinics: boolean;
  availableRoles: any[];
  loadingRoles?: boolean;
}

const AdminUserModal: React.FC<AdminUserModalProps> = ({ 
  visible, 
  admin, 
  isSubmitting, 
  form, 
  onCancel, 
  onSubmit,
  doctors,
  loadingDoctors,
  clinics,
  loadingClinics,
  availableRoles,
  loadingRoles = false
}) => {
  const { t, currentLanguage } = useTranslation();
  const isEdit = !!admin;

  // Ensure form is properly populated when modal opens
  useEffect(() => {
    if (visible) {
      // Small delay to ensure form is ready
      setTimeout(() => {
        if (admin && isEdit) {
          // Editing existing admin user
          form.setFieldsValue({
            ...admin,
            role_id: admin.role_id, // Use role_id to link to AdminUserRole
            created_at: admin.created_at ? dayjs(admin.created_at) : null
          });
        } else {
          // Creating new admin user - set default values
          form.setFieldsValue({
            is_active: true,
            permissions: []
          });
        }
      }, 100);
    }
  }, [visible, admin, isEdit, form]);

  return (
    <Modal
      title={isEdit ? t('adminUsers.editAdminUser') : t('adminUsers.createAdminUser')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('adminUsers.firstName')}
              name="firstname"
              rules={[{ required: true, message: t('adminUsers.pleaseInputFirstName') }]}
            >
              <Input placeholder={t('adminUsers.enterFirstName')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('adminUsers.lastName')}
              name="lastname"
              rules={[{ required: true, message: t('adminUsers.pleaseInputLastName') }]}
            >
              <Input placeholder={t('adminUsers.enterLastName')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('adminUsers.email')}
              name="email"
              rules={[
                { required: true, message: t('adminUsers.pleaseInputEmail') },
                { type: 'email', message: t('adminUsers.pleaseEnterValidEmail') }
              ]}
            >
              <Input placeholder={t('adminUsers.enterEmail')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('adminUsers.phoneNumber')}
              name="phone_number"
            >
              <Input placeholder={t('adminUsers.enterPhoneNumber')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={20}>
            <Form.Item
              label={t('adminUsers.role')}
              name="role_id"
              rules={[{ required: true, message: t('adminUsers.pleaseSelectRole') }]}
            >
              <Select 
                placeholder={t('adminUsers.selectRole')}
                loading={loadingRoles}
                showSearch
                notFoundContent={loadingRoles ? 'Loading...' : 'No roles found'}
                filterOption={(input, option) => {
                  const role = availableRoles.find((r: any) => r.id === option?.value);
                  if (!role) return false;
                  return role.name.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {availableRoles.length === 0 && !loadingRoles ? (
                  <Option disabled value="">
                    No roles available
                  </Option>
                ) : (
                  availableRoles.map((role: any) => (
                    <Option key={role.id} value={role.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 500 }}>{role.name}</div>
                        {role.description && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {role.description}
                          </div>
                        )}
                      </div>
                    </Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label={t('common.status')}
              name="is_active"
              valuePropName="checked"
            >
              <Switch checkedChildren={t('adminUsers.statuses.active')} unCheckedChildren={t('adminUsers.statuses.inactive')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={t('adminUsers.linkedDoctor')}
              name="doctor_id"
              extra={
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {form.getFieldValue('doctor_id') 
                    ? t('adminUsers.clickClearToDisconnect')
                    : t('adminUsers.selectDoctorToConnect')
                  }
                </div>
              }
            >
              <Select 
                placeholder={t('adminUsers.selectDoctor')}
                allowClear
                showSearch
                loading={loadingDoctors}
                filterOption={(input, option) => {
                  const doctor = doctors.find((d: any) => d.id === option?.value);
                  if (!doctor) return false;
                  const searchText = input.toLowerCase();
                  return (
                    doctor.first_name_en.toLowerCase().includes(searchText) ||
                    doctor.last_name_en.toLowerCase().includes(searchText) ||
                    doctor.first_name_zh_hk.toLowerCase().includes(searchText) ||
                    doctor.last_name_zh_hk.toLowerCase().includes(searchText) ||
                    doctor.specialization.toLowerCase().includes(searchText) ||
                    doctor.license_number.toLowerCase().includes(searchText)
                  );
                }}
              >
                {doctors.map((doctor: any) => (
                  <Option key={doctor.id} value={doctor.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 500 }}>
                          {doctor.first_name_en} {doctor.last_name_en}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {getSpecializationLabel(doctor.specialization, currentLanguage)} • {doctor.license_number}
                        </div>
                      </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={t('adminUsers.linkedClinic')}
              name="clinic_id"
              extra={
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {form.getFieldValue('clinic_id') 
                    ? t('adminUsers.clickClearToDisconnect')
                    : t('adminUsers.selectClinicToConnect')
                  }
                </div>
              }
            >
              <Select 
                placeholder={t('adminUsers.selectClinic')}
                allowClear
                showSearch
                loading={loadingClinics}
                filterOption={(input, option) => {
                  const clinic = clinics.find((c: any) => c.id === option?.value);
                  if (!clinic) return false;
                  const searchText = input.toLowerCase();
                  return (
                    (clinic.internal_name || '').toLowerCase().includes(searchText) ||
                    (clinic.name_en || '').toLowerCase().includes(searchText) ||
                    (clinic.name_zh_hk || '').toLowerCase().includes(searchText) ||
                    (clinic.address || '').toLowerCase().includes(searchText) ||
                    (clinic.district || '').toLowerCase().includes(searchText) ||
                    (clinic.region || '').toLowerCase().includes(searchText)
                  );
                }}
              >
                {clinics.map((clinic: any) => (
                  <Option key={clinic.id} value={clinic.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: 500 }}>
                        {clinic.internal_name || clinic.name_zh_hk}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {!isEdit && (
          <Form.Item
            label={t('adminUsers.password')}
            name="password"
            rules={[{ required: true, message: t('adminUsers.pleaseInputPassword') }]}
          >
            <Input.Password placeholder={t('adminUsers.enterPassword')} />
          </Form.Item>
        )}

        <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isSubmitting}
              icon={isSubmitting ? <LoadingOutlined /> : <SaveOutlined />}
            >
              {isSubmitting ? t('adminUsers.saving') : (isEdit ? t('adminUsers.update') : t('adminUsers.create'))}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdminUserModal;
